import React, {type ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {useBulkActionDialog} from 'src/components/bulk-tool/useBulkActionDialog';
import {SelectionHeader, SelectionRow} from 'src/components/data-table-selection/components';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {UserLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {NO_RESULTS_MESSAGE, ROUTES, USER_TABS} from 'src/constants';
import {useUserStatuses} from 'src/hooks/dictionaries';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {UpdateAdminRoleTool} from 'src/modules/bulk-update';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Cell, CellOptions, Column, DataTable, HeaderCellOptions, RowsParams, TableHeader, type TextAlign} from '@pb/kit-component-data-table-v2';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {ProfileTabs} from '../components/tabs';

import {AssignUserModal} from './components/assign-user/assign-user-modal';
import {AssignedUsersHeadline} from './components/assigned-users-headline';
import {AssignedUsersPagination} from './components/assigned-users-pagination';
import {EmailHeader} from './components/headers/email.header';
import {SortHeader} from './components/headers/sort-header';
import {UpdateUserRights} from './components/update-user-rights/update-user-rights';
import {COLUMNS, COLUMNS_DATA, SELECTION_KEY, SORT_FIELDS} from './constants';
import {useGetAssignedUsers} from './hooks/useGetAssignedUsers';
import {useGetRoleProfile} from './hooks/useGetRoleProfile';
import {assignedUsersActions} from './slices/assigned-users-slice';
import {assignedUsersStateActions, assignedUsersStateSelector} from './slices/assigned-users-state-slice';


const EMPTY_RESULTS_MESSAGE = 'No users have been assigned to this role yet.';

export const AssignedUsersWrapper = () => {
    const dispatch = useDispatch();
    const queryParams = useParams<{roleId: string}>();
    const {searchCriteria} = useSelector(assignedUsersStateSelector);

    useEffect(() => {
        if (queryParams.roleId) {
            const profileId = +queryParams.roleId;
            dispatch(assignedUsersStateActions.setSearchCriteria({profileId}));
        }
    }, [queryParams, dispatch]);

    if (searchCriteria.profileId === undefined) {
        return null;
    }

    return <AssignedUsersTab profileId={searchCriteria.profileId}/>;
};

type Props = {
    profileId: number,
};

const AssignedUsersTab = ({profileId}: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        loadingError,
        result,
        pagination,
        cacheKeys,
    } = useGetAssignedUsers();

    const {
        data: profile,
        isLoading: isProfileLoading,
        loadingError: profileLoadingError,
    } = useGetRoleProfile({profileId});

    const [shownAssignUserModal, setShownAssignUserModal] = useState(false);
    const {onClose: closeAssignUserModal, onPendingChange} = useBulkActionDialog(() => setShownAssignUserModal(false));

    const {getUserStatusByCode} = useUserStatuses();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const refreshResults = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(assignedUsersActions.requireRefresh());
    }, [dispatch]);

    const setActiveTab = useCallback((tab: string, recordId?: number) => {
        if (recordId) {
            navigate(`${ROUTES.PROFILE}/${profileId}/${tab}/${recordId}`);
        } else {
            navigate(`${ROUTES.PROFILE}/${profileId}/${tab}`);
        }
    }, [profileId, navigate]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(assignedUsersStateActions.clearData());
            dispatch(assignedUsersActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: result?.data.length || 0});
    const entityIds = useMemo(() => result?.data.map(({userId}) => userId) || [], [result?.data]);

    return (
        <ServerResponseHandler serverError={loadingError || profileLoadingError} sectionName="Assigned Users">
            <div>
                <StickyElement>
                    <AssignedUsersHeadline
                        isLoading={isProfileLoading}
                        profileName={profile.profileName}
                        onAddUser={() => setShownAssignUserModal(true)}
                    />
                    <ProfileTabs activeTab="assigned-users" onTabChange={setActiveTab}/>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <ToolsRibbonComponent {...{
                        rows: entityIds,
                        selectionKey: SELECTION_KEY,
                        totalCount: result?.totalCount || 0}}>
                        {selectedUserIds => (
                            <Group alignItems="center">
                                <UpdateAdminRoleTool userIds={selectedUserIds as number[]} onDone={refreshResults}/>
                                <UpdateUserRights userIds={selectedUserIds as number[]}/>
                            </Group>
                        )}
                    </ToolsRibbonComponent>
                    <DataTable
                        onStopResize={onStopResize}
                        getHeaderTopOffset={() => 145}
                        showNoDataMsg={!!result && !result.data.length}
                        tableMessage={Object.keys(cacheKeys.searchCriteria).length
                            ? NO_RESULTS_MESSAGE
                            : EMPTY_RESULTS_MESSAGE}
                        data={{
                            columns: COLUMNS_DATA.map((column: Column) => ({
                                ...column,
                                width: getColumnWidthByColumnId(column.columnId),
                            })),
                            rows: result?.data.map(({userId, ...columnValues}) => ({id: userId, columnValues})),
                        }}
                        headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                            const {columnId} = column;
                            switch (columnId) {
                                case COLUMNS.EMAIL:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <MainColumnHeader
                                                selection={(
                                                    <SelectionHeader
                                                        selectionKey={SELECTION_KEY}
                                                        totalCount={result?.totalCount || 0}
                                                        entityIds={entityIds}
                                                    />
                                                )}
                                                maxRowNumber={maxRowNumber}>
                                                <EmailHeader/>
                                            </MainColumnHeader>
                                        </Cell>
                                    );
                                default:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <SortHeader column={columnId} sortField={SORT_FIELDS[columnId]}/>
                                            </TableHeader>
                                        </Cell>
                                    );
                            }
                        }}
                        renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                            const {rowIndex, column: {columnId}} = rowsParams;
                            const value = rowsParams.row.columnValues[columnId] as string;
                            const rowId = +rowsParams.row.id;
                            const column = rowsParams.column;
                            const cellKey = rowIndex + columnId.toString();
                            const rowNumber = getPaginatedRowNumber({
                                ...pagination,
                                index: rowIndex + 1,
                            });

                            const getBasicCell = (content: ReactNode, align?: TextAlign) => {
                                return (
                                    <Cell
                                        {...cellOptions}
                                        key={cellKey}
                                        column={column}
                                        align={align}>
                                        {content}
                                    </Cell>
                                );
                            };

                            switch (columnId) {
                                case COLUMNS.EMAIL:
                                    return (
                                        <MainColumnCell
                                            key={cellKey}
                                            {...{
                                                selection: (
                                                    <SelectionRow
                                                        index={rowIndex}
                                                        entityId={rowId}
                                                        totalCount={result?.totalCount || 0}
                                                        rows={entityIds}
                                                        selectionKey={SELECTION_KEY}
                                                    />
                                                ),
                                                rowNumber,
                                                rowId,
                                                rowsParams,
                                                cellOptions: {...cellOptions, column},
                                                maxRowNumber,
                                            }}>
                                            <UserLink
                                                place="Role Profile"
                                                userId={rowId}
                                                caption={value}
                                                route={USER_TABS.ROLE_SETTINGS.name}
                                            />
                                        </MainColumnCell>
                                    );
                                case COLUMNS.STATUS:
                                    return getBasicCell(getUserStatusByCode(value)?.description);
                                default:
                                    return getBasicCell(<EllipsisTextHint title={value}>{value}</EllipsisTextHint>);
                            }
                        }}
                        paginationStripe={<AssignedUsersPagination totalCount={result?.totalCount || 0}/>}
                    />
                    <Dialog
                        shown={shownAssignUserModal}
                        setShown={closeAssignUserModal}
                        wrapperProps={{style: {width: 800}}}>
                        <AssignUserModal
                            profileId={profileId}
                            onClose={closeAssignUserModal}
                            onSubmit={() => setShownAssignUserModal(false)}
                            onPendingChange={onPendingChange}
                            onDone={refreshResults}
                        />
                    </Dialog>
                </BoxLoading>
            </div>
        </ServerResponseHandler>
    );
};
